import React from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
  alpha,
  Paper,
  useTheme,
  Fade,
  Tooltip,
} from "@mui/material";
import CommonTable from "../../utils/CommonTable";
import TaxField from "../../utils/TextField/TaxField";
import { formatAmount } from "../../../utils/hooks/dataHandler";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AssuredWorkloadIcon from "@mui/icons-material/AssuredWorkload";

export const fieldMap = {
  "gross-trade-branch": "grossTradeBranchProfits",
  "total-revenue": "totalRevenue",
  "adjusted-profit-loss": "adjustedProfitLoss",
  "other-sourced-income": "otherSourcedIncome",
  "statutory-income": "statutoryIncome",
  "tax-payable": "taxPayable",
};

const sectionIconStyle = {
  fontSize: 20,
  color: "primary.main",
};

const sectionIconMap = {
  A: <ReceiptLongIcon sx={sectionIconStyle} />,
  B: <ReceiptLongIcon sx={sectionIconStyle} />,
  C: <ReceiptLongIcon sx={sectionIconStyle} />,
  D: <Inventory2Icon sx={sectionIconStyle} />,
  E: <Inventory2Icon sx={sectionIconStyle} />,
  F: <AssuredWorkloadIcon sx={sectionIconStyle} />,
};

const MainCompFtcBreakdown = ({
  data,
  breakdownRows,
  onChange,
  canEdit = true,
}) => {
  const theme = useTheme();

  const getTableColumns = () => [
    {
      key: "description",
      label: "Main Computation Section",
      headerAlign: "left",
      colAlign: "left",
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              border: (theme) =>
                `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            }}
          >
            {sectionIconMap[row.section]}
          </Box>
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Typography
                variant='body1'
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  fontSize: "0.95rem",
                }}
              >
                {row.description}
              </Typography>
              {row.info && (
                <Tooltip
                  title={
                    <Box
                      component='div'
                      sx={{
                        whiteSpace: "pre-line",
                        fontSize: "0.875rem",
                        lineHeight: 1.6,
                      }}
                    >
                      {row.info}
                    </Box>
                  }
                  arrow
                  placement='top'
                >
                  <InfoOutlinedIcon
                    sx={{
                      color: "info.main",
                      fontSize: 18,
                      cursor: "help",
                      "&:hover": {
                        color: "info.dark",
                      },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
            {row.country && (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{
                  mt: 0.5,
                  display: "block",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                }}
              >
                Country: {row.country}
              </Typography>
            )}
            {row.reference && (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mt: 0.5, fontSize: "0.75rem" }}
              >
                {row.reference}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      key: "section",
      label: "Section ID",
      headerAlign: "center",
      colAlign: "center",
      render: (row) => (
        <Chip
          label={row.section}
          color='primary'
          size='small'
          variant='outlined'
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            height: 28,
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
            "&:hover": {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.12),
            },
            transition: "all 0.2s ease-in-out",
          }}
        />
      ),
    },
    {
      key: "value",
      label: "Amount",
      headerAlign: "right",
      colAlign: "right",
      render: (row) => (
        <Box sx={{ minWidth: 140 }}>
          <TaxField
            value={row.isReadOnly ? row.value : breakdownRows[fieldMap[row.id]]}
            onChange={onChange}
            reference={{
              field: fieldMap[row.id],
            }}
            canEdit={canEdit}
            disabled={row.isReadOnly}
            decimalScale={row.id === "tax-payable" ? 2 : 0}
            sx={{
              "& .MuiInputBase-input": {
                textAlign: "right",
                backgroundColor: row.isReadOnly
                  ? (theme) => alpha(theme.palette.grey[100], 0.8)
                  : "inherit",
                fontWeight: 500,
                fontSize: "0.9rem",
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "&:hover": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.5),
                  },
                },
              },
            }}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      {/* Main Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          background: `linear-gradient(145deg, ${
            theme.palette.background.paper
          }, ${alpha(theme.palette.primary.main, 0.01)})`,
        }}
      >
        <CommonTable
          rows={data?.rows}
          columns={getTableColumns()}
          noRowsHint='No FTC breakdown data available yet.'
        />
      </Paper>

      {/* Summary Section */}
      <Box>
        <Stack
          direction='row'
          spacing={2}
          sx={{
            flexWrap: "wrap",
            gap: 2,
            py: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.info.main,
                0.08
              )}, ${alpha(theme.palette.info.main, 0.04)})`,
              minWidth: 200,
            }}
          >
            <Typography
              variant='caption'
              sx={{
                color: "info.main",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontSize: "0.7rem",
              }}
            >
              Trade/Branch Profits
            </Typography>
            <Typography
              variant='h6'
              sx={{
                fontWeight: 700,
                color: "text.primary",
                mt: 0.5,
                fontSize: "1rem",
              }}
            >
              {formatAmount(data?.computedData?.grossTradeBranchProfits)}
            </Typography>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.warning.main,
                0.08
              )}, ${alpha(theme.palette.warning.main, 0.04)})`,
              minWidth: 200,
            }}
          >
            <Typography
              variant='caption'
              sx={{
                color: "warning.main",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontSize: "0.7rem",
              }}
            >
              Other Sourced Income
            </Typography>
            <Typography
              variant='h6'
              sx={{
                fontWeight: 700,
                color: "text.primary",
                mt: 0.5,
                fontSize: "1rem",
              }}
            >
              {formatAmount(data?.computedData?.otherSourcedIncome)}
            </Typography>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.08
              )}, ${alpha(theme.palette.primary.main, 0.04)})`,
              minWidth: 200,
            }}
          >
            <Typography
              variant='caption'
              sx={{
                color: "primary.main",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontSize: "0.7rem",
              }}
            >
              Statutory Income
            </Typography>
            <Typography
              variant='h6'
              sx={{
                fontWeight: 700,
                color: "text.primary",
                mt: 0.5,
                fontSize: "1rem",
              }}
            >
              {formatAmount(data?.computedData?.statutoryIncome)}
            </Typography>
          </Paper>
        </Stack>

        {/* Info Alert */}
        {!data?.hasLockedData && (
          <Fade in={!data?.hasLockedData} timeout={300}>
            <Box>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.info.main,
                    0.08
                  )}, ${alpha(theme.palette.info.main, 0.04)})`,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: `linear-gradient(90deg, ${
                      theme.palette.info.main
                    }, ${alpha(theme.palette.info.main, 0.6)})`,
                  },
                }}
              >
                <Stack direction='row' spacing={2} alignItems='flex-start'>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 2,
                      bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
                      flexShrink: 0,
                    }}
                  >
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: "info.main",
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant='subtitle2'
                      sx={{
                        fontWeight: 600,
                        color: "info.main",
                        mb: 0.5,
                        fontSize: "0.9rem",
                      }}
                    >
                      Data Required
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.5,
                        fontSize: "0.85rem",
                      }}
                    >
                      To begin the calculation, please review your Foreign
                      Income table and lock at least one row. Locked rows will
                      be included in the main computation and auto-calculated
                      fields below.
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Box>
          </Fade>
        )}
        {/* Success Alert */}
        {data?.hasLockedData && (
          <Fade in={true} timeout={500}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.success.main,
                  0.08
                )}, ${alpha(theme.palette.success.main, 0.04)})`,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: `linear-gradient(90deg, ${
                    theme.palette.success.main
                  }, ${alpha(theme.palette.success.main, 0.6)})`,
                },
              }}
            >
              <Stack direction='row' spacing={2} alignItems='flex-start'>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 2,
                    bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
                    flexShrink: 0,
                  }}
                >
                  <CheckCircleOutlinedIcon
                    sx={{
                      fontSize: 20,
                      color: "success.main",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant='subtitle2'
                    sx={{
                      fontWeight: 600,
                      color: "success.main",
                      mb: 0.5,
                      fontSize: "0.9rem",
                    }}
                  >
                    Ready for FTC Calculation
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.5,
                      fontSize: "0.85rem",
                    }}
                  >
                    Statutory Income:{" "}
                    {formatAmount(data?.computedData?.statutoryIncome)} • Tax
                    Payable: {formatAmount(data?.computedData?.taxPayable)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Fade>
        )}
      </Box>
    </Box>
  );
};

export default MainCompFtcBreakdown;
